<template>
    <div class="reverse-card-ops-panel fade-in">
        <MaintCollapsibleBanner
            title="反序卡号补上报扣款"
            icon="el-icon-bank-card"
            storage-key="reverseCardPayBatch"
        >
            <div class="desc-item">
                <span class="desc-badge badge-primary">日志输入格式</span>
                <span>请粘贴终端消费日志，每行中应包含 <code>进入交易扣款参数【{JSON}】</code>。</span>
            </div>
            <div class="desc-item">
                <span class="desc-badge badge-success">执行逻辑</span>
                <span>本系统与后台 <code>userPricePayReverseCardBatch</code> 逻辑完全一致：反序卡号后自动转换为真实卡号并进行二次扣款。</span>
            </div>
            <div class="desc-item">
                <span class="desc-badge badge-warning">自动拆分分批</span>
                <span>单次大批量执行按每批最多 <strong>1000 条</strong> 自动分批下发，以防单次数据库连接超时。</span>
            </div>
        </MaintCollapsibleBanner>

        <el-card shadow="never" class="step-card branch-card">
            <div slot="header" class="card-header-styled">
                <i class="el-icon-document"></i>
                <span>日志输入源控制台终端</span>
            </div>
            
            <!-- 极简酷炫终端视窗 -->
            <div class="editor-terminal-container">
                <div class="terminal-header">
                    <div class="terminal-dots">
                        <span class="term-dot dot-red"></span>
                        <span class="term-dot dot-yellow"></span>
                        <span class="term-dot dot-green"></span>
                    </div>
                    <span class="terminal-title">device_transaction_log.log</span>
                </div>
                <el-input
                    v-model="logText"
                    type="textarea"
                    :rows="10"
                    placeholder="粘贴 docs/反转卡号消费记录/ 下样例 txt 全文，或从日志平台复制带「进入交易扣款参数」的行"
                    class="terminal-textarea"
                />
            </div>

            <div class="actions-wrapper">
                <el-button size="medium" :loading="previewLoading" :disabled="!logText.trim()" @click="doPreview" class="btn-modern btn-secondary">
                    <i class="el-icon-zoom-in"></i> 解析日志预览
                </el-button>
                <el-button size="medium" type="warning" :loading="executeLoading" :disabled="!logText.trim()" @click="doDryRun" class="btn-modern btn-warning">
                    <i class="el-icon-magic-stick"></i> 试运行（不扣款）
                </el-button>
                <el-button size="medium" type="primary" :loading="executeLoading" :disabled="!logText.trim()" @click="doExecute" class="btn-modern btn-primary">
                    <i class="el-icon-wallet"></i> 执行批量扣款
                </el-button>
            </div>
        </el-card>

        <!-- 数据解析结果看板 -->
        <el-card v-if="previewResult" shadow="never" class="step-card branch-card-teal">
            <div slot="header" class="card-header-styled">
                <i class="el-icon-pie-chart"></i>
                <span>解析与匹配统计结果</span>
            </div>

            <div class="stat-dashboard">
                <div class="stat-card stat-blue">
                    <div class="stat-info">
                        <span class="stat-label">有效待补笔数</span>
                        <span class="stat-value">{{ previewResult.ParsedCount }}<span class="unit">条</span></span>
                    </div>
                    <div class="stat-icon-bg"><i class="el-icon-circle-check"></i></div>
                </div>
                <div class="stat-card stat-emerald">
                    <div class="stat-info">
                        <span class="stat-label">合计补扣金额</span>
                        <span class="stat-value">{{ previewResult.TotalPayMoney }}<span class="unit">元</span></span>
                    </div>
                    <div class="stat-icon-bg"><i class="el-icon-money"></i></div>
                </div>
                <div class="stat-card stat-orange">
                    <div class="stat-info">
                        <span class="stat-label">跳过不匹配行</span>
                        <span class="stat-value">{{ previewResult.SkippedLineCount }}<span class="unit">行</span></span>
                    </div>
                    <div class="stat-icon-bg"><i class="el-icon-document-remove"></i></div>
                </div>
                <div class="stat-card stat-gray">
                    <div class="stat-info">
                        <span class="stat-label">无效JSON/去重</span>
                        <span class="stat-value">{{ previewResult.DuplicateCount + previewResult.InvalidJsonCount }}<span class="unit">条</span></span>
                    </div>
                    <div class="stat-icon-bg"><i class="el-icon-delete"></i></div>
                </div>
            </div>

            <!-- 表格明细 -->
            <div v-if="previewResult.PreviewItems && previewResult.PreviewItems.length" class="table-section">
                <div class="table-title"><i class="el-icon-tickets"></i> 日志解析明细样例展示 (仅提取部分进行展示)</div>
                <el-table :data="previewResult.PreviewItems" size="medium" max-height="320" class="custom-table" border>
                    <el-table-column prop="Index" label="#" width="60" align="center" />
                    <el-table-column prop="ReverseCardNo" label="反序卡号" width="140" align="center" />
                    <el-table-column prop="RealCardNo" label="真实卡号" width="140" align="center" />
                    <el-table-column prop="PayMoney" label="金额" width="90" align="right">
                        <template slot-scope="scope">
                            <span class="money-highlight">{{ scope.row.PayMoney }} 元</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="PayTime" label="支付时间" width="180" align="center" />
                    <el-table-column prop="DeviceSerialNo" label="设备序列号" show-overflow-tooltip />
                    <el-table-column prop="Transactions" label="交易类型" width="100" align="center">
                        <template slot-scope="scope">
                            <el-tag size="mini" type="info" effect="plain">{{ scope.row.Transactions }}</el-tag>
                        </template>
                    </el-table-column>
                </el-table>
                <p v-if="previewResult.ParsedCount > previewResult.PreviewItems.length" class="sample-tip">
                    <i class="el-icon-info"></i> 提示：当前仅展示前 {{ previewResult.PreviewItems.length }} 条作为扣款确认预览。真实运行将对全部 {{ previewResult.ParsedCount }} 条记录下发扣款指令。
                </p>
            </div>
        </el-card>

        <!-- 批量执行结果 -->
        <el-card v-if="executeResult" shadow="never" class="step-card branch-card-orange">
            <div slot="header" class="card-header-styled">
                <i class="el-icon-document-checked"></i>
                <span>批量执行报告清单</span>
            </div>
            
            <div class="report-message-box">
                <span class="report-badge" :class="executeResult.DryRun ? 'badge-dry' : 'badge-live'">
                    {{ executeResult.DryRun ? '试运行' : '真实写入' }}
                </span>
                <span class="report-text">{{ executeResult.Message }}</span>
            </div>

            <div v-if="executeResult.Batches && executeResult.Batches.length" class="table-section">
                <div class="table-title"><i class="el-icon-s-operation"></i> 自动拆分批次处理进度汇总</div>
                <el-table :data="executeResult.Batches" size="medium" max-height="180" class="custom-table" border>
                    <el-table-column prop="BatchIndex" label="处理批次" width="100" align="center">
                        <template slot-scope="scope">
                            <span class="batch-badge">第 {{ scope.row.BatchIndex }} 批</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="ItemCount" label="本批总数" width="120" align="right" />
                    <el-table-column prop="SuccessCount" label="扣款成功数" width="120" align="right">
                        <template slot-scope="scope">
                            <span class="success-count">{{ scope.row.SuccessCount }} 笔</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="FailedCount" label="扣款失败数" width="120" align="right">
                        <template slot-scope="scope">
                            <span class="failed-count" :class="{'has-fail': scope.row.FailedCount > 0}">{{ scope.row.FailedCount }} 笔</span>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <!-- 扣款失败详细排查表 -->
            <div v-if="!executeResult.DryRun && executeResult.FailedItems && executeResult.FailedItems.length" class="table-section error-section">
                <div class="table-title error-title"><i class="el-icon-warning"></i> 批量扣款失败账单明细</div>
                <el-table :data="executeResult.FailedItems" size="medium" max-height="280" class="custom-table fail-table" border>
                    <el-table-column prop="GlobalIndex" label="#" width="60" align="center" />
                    <el-table-column prop="CardNo" label="反序卡号" width="140" align="center" />
                    <el-table-column prop="PayMoney" label="扣款金额" width="100" align="right">
                        <template slot-scope="scope">
                            <span class="fail-money">{{ scope.row.PayMoney }} 元</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="PayTime" label="支付交易时间" width="180" align="center" />
                    <el-table-column prop="Status" label="网络状态码" width="100" align="center">
                        <template slot-scope="scope">
                            <el-tag size="mini" type="danger" effect="plain">{{ scope.row.Status }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="Msg" label="失败原因及错误信息" show-overflow-tooltip />
                </el-table>
            </div>
        </el-card>
    </div>
</template>

<script>
import { consumerOpsPreviewParseLog, consumerOpsExecuteFromLog } from '../../../api/modules/consumption';
import MaintCollapsibleBanner from '../components/MaintCollapsibleBanner';

/**
 * 反序卡号日志补扣款运维模块。创建于 2026-05-30
 * 样式于 2026-05-30 重构为极奢现代控制台风格
 */
export default {
    name: 'ReverseCardPayBatch',
    components: { MaintCollapsibleBanner },
    data() {
        return {
            logText: '',
            previewLoading: false,
            executeLoading: false,
            previewResult: null,
            executeResult: null
        };
    },
    methods: {
        doPreview() {
            this.previewLoading = true;
            consumerOpsPreviewParseLog({ logText: this.logText }).then(res => {
                this.previewLoading = false;
                if (res.data && res.data.success) {
                    this.previewResult = res.data.response;
                    this.$message.success(`解析 ${this.previewResult.ParsedCount} 条`);
                } else {
                    this.$message.error((res.data && res.data.msg) || '解析失败');
                }
            }).catch(() => { this.previewLoading = false; });
        },
        doDryRun() {
            this.confirmRun('试运行：仅统计条数与金额，不扣款', () => this.runExecute(true));
        },
        doExecute() {
            this.confirmRun('确认执行批量反序卡号扣款？将真实写库，请先在测试环境或少量日志验证。', () => this.runExecute(false));
        },
        runExecute(dryRun) {
            this.executeLoading = true;
            consumerOpsExecuteFromLog({ logText: this.logText, dryRun }).then(res => {
                this.executeLoading = false;
                if (res.data && res.data.success) {
                    this.executeResult = res.data.response;
                    this.$message.success(res.data.msg || '完成');
                    if (!dryRun) this.doPreview();
                } else {
                    this.$message.error((res.data && res.data.msg) || '执行失败');
                }
            }).catch(() => { this.executeLoading = false; });
        },
        confirmRun(title, fn) {
            this.$confirm(title, '提示', { type: 'warning' }).then(() => fn()).catch(() => {});
        }
    }
};
</script>

<style scoped>
/* 根容器：在 Hub flex 布局内占满高度并允许纵向滚动 */
.reverse-card-ops-panel {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    padding-bottom: 12px;
    scrollbar-width: thin;
    scrollbar-color: #94a3b8 #f1f5f9;
}

.reverse-card-ops-panel::-webkit-scrollbar {
    width: 6px;
}

.reverse-card-ops-panel::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 4px;
}

.reverse-card-ops-panel::-webkit-scrollbar-thumb:hover {
    background: #64748b;
}

.reverse-card-ops-panel::-webkit-scrollbar-track {
    background: #f1f5f9;
}

/* 全局页面淡入与精致排版 */
.fade-in {
    animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
}

.fade-in-up {
    animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
}

/* 卡片 */
.step-card {
    border-radius: 12px !important;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02) !important;
    margin-top: 16px;
    transition: box-shadow 0.3s;
}

::v-deep .step-card .el-card__header {
    padding: 12px 18px;
}

::v-deep .step-card .el-card__body {
    padding: 14px 18px;
}

.step-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02) !important;
}

.branch-card { border-left: 4px solid #6366f1 !important; }
.branch-card-teal { border-left: 4px solid #0d9488 !important; }
.branch-card-orange { border-left: 4px solid #ea580c !important; }

.card-header-styled {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
}

.card-header-styled i {
    color: #6366f1;
    font-size: 16px;
}

/* 极简酷炫终端控制台输入区（已重构为高雅亮色风格，避免视觉突兀） */
.editor-terminal-container {
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #cbd5e1;
    overflow: hidden;
    margin-bottom: 20px;
    box-shadow: 0 6px 16px rgba(148, 163, 184, 0.08);
    transition: all 0.3s;
}

.editor-terminal-container:focus-within {
    border-color: #6366f1;
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.12);
}

.terminal-header {
    background: #f1f5f9;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #cbd5e1;
}

.terminal-dots {
    display: flex;
    gap: 6px;
}

.term-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.dot-red { background: #ef4444; }
.dot-yellow { background: #eab308; }
.dot-green { background: #22c55e; }

.terminal-title {
    font-family: Consolas, Monaco, monospace;
    font-size: 11px;
    color: #475569;
    font-weight: 600;
    opacity: 0.8;
}

/* 输入框的终端内化穿透改写 */
::v-deep .terminal-textarea.el-textarea .el-textarea__inner {
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    color: #0f172a !important; /* 调整为优雅深墨黑，极其养眼 */
    font-family: 'Consolas', 'Courier New', Courier, Monaco, monospace !important;
    font-size: 13px !important;
    line-height: 1.6 !important;
    padding: 16px !important;
    resize: vertical;
    outline: none !important;
}

::v-deep .terminal-textarea.el-textarea .el-textarea__inner::placeholder {
    color: #94a3b8;
}

/* 按钮操作排版与微动效 */
.actions-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 4px;
}

.btn-modern {
    border-radius: 8px !important;
    font-weight: 600 !important;
    padding: 10px 18px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.btn-modern i {
    font-size: 14px;
}

.btn-modern:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-modern:active {
    transform: translateY(0);
}

/* 辅助灰色按钮 */
.btn-secondary {
    background: #f1f5f9 !important;
    color: #475569 !important;
    border: 1px solid #cbd5e1 !important;
}

.btn-secondary:hover {
    background: #e2e8f0 !important;
    color: #1e293b !important;
}

/* 警告黄色按钮 */
.btn-warning {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%) !important;
    border: none !important;
    color: #ffffff !important;
}

.btn-warning:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3) !important;
}

/* 主运行蓝色按钮 */
.btn-primary {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    border: none !important;
    color: #ffffff !important;
}

.btn-primary:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
}

/* 指标卡片看板 */
.stat-dashboard {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
}

.stat-card {
    position: relative;
    padding: 20px;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px -1px rgba(0, 0, 0, 0.01);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 2;
}

.stat-label {
    font-size: 12px;
    font-weight: 600;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.1;
    font-family: 'Outfit', sans-serif;
}

.stat-value .unit {
    font-size: 13px;
    font-weight: 500;
    margin-left: 4px;
}

.stat-icon-bg {
    font-size: 40px;
    opacity: 0.12;
    z-index: 1;
    position: absolute;
    right: 16px;
    bottom: 8px;
}

/* 调色板 - 指标卡 */
.stat-blue {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e40af;
}
.stat-emerald {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    color: #166534;
}
.stat-orange {
    background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
    color: #c2410c;
}
.stat-gray {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    color: #475569;
}

/* 表格区域美化 */
.table-section {
    margin-top: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
}

.table-title {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.table-title i {
    color: #6366f1;
}

/* 深度美化 Element 表格 */
::v-deep .custom-table.el-table {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

::v-deep .custom-table.el-table th {
    background: #f8fafc !important;
    color: #475569 !important;
    font-weight: 600 !important;
    height: 42px;
    padding: 0;
}

::v-deep .custom-table.el-table td {
    padding: 8px 0;
    color: #334155;
}

::v-deep .custom-table.el-table--enable-row-hover .el-table__body tr:hover > td {
    background-color: #f1f5f9 !important;
}

.money-highlight {
    font-weight: 600;
    color: #059669;
}

.sample-tip {
    font-size: 12px;
    color: #64748b;
    margin-top: 12px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.sample-tip i {
    color: #3b82f6;
}

/* 批量执行结果 */
.report-message-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.report-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
}

.badge-dry {
    background: rgba(217, 119, 6, 0.1);
    color: #d97706;
    border: 1px solid rgba(217, 119, 6, 0.2);
}

.badge-live {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.report-text {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
}

.batch-badge {
    background: #e0e7ff;
    color: #4338ca;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
}

.success-count {
    color: #10b981;
    font-weight: 600;
}

.failed-count {
    color: #64748b;
    font-weight: 500;
}

.failed-count.has-fail {
    color: #ef4444;
    font-weight: 600;
}

/* 异常排查专区 */
.error-section {
    border-color: #fecaca;
    background: #fff5f5;
}

.error-title {
    color: #991b1b;
}

.error-title i {
    color: #ef4444;
}

.fail-money {
    color: #dc2626;
    font-weight: 600;
}
</style>

